import React, { useState } from 'react';
import { MapPin, Phone, Mail, Clock, Send, CheckCircle } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { companyInfo } from '../mock';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Mock submission - in real app this would send to backend
    console.log('Form submitted:', formData);
    setSubmitted(true);
    setTimeout(() => {
      setSubmitted(false);
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
      });
    }, 3000);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <section className="bg-gradient-to-br from-slate-900 to-blue-900 text-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">Hubungi Kami</h1>
            <p className="text-xl text-blue-100">
              Kami siap membantu mewujudkan proyek Anda
            </p>
          </div>
        </div>
      </section>

      {/* Contact Info & Form */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12">
              {/* Contact Cards */}
              <Card className="bg-white border-2 border-slate-200 hover:border-blue-400 hover:shadow-lg transition-all duration-300">
                <CardContent className="p-6 text-center">
                  <div className="w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <MapPin className="w-7 h-7 text-blue-600" />
                  </div>
                  <h3 className="text-lg font-bold text-slate-900 mb-3">Alamat</h3>
                  <p className="text-slate-600 text-sm leading-relaxed">{companyInfo.address}</p>
                </CardContent>
              </Card>
              
              <Card className="bg-white border-2 border-slate-200 hover:border-blue-400 hover:shadow-lg transition-all duration-300">
                <CardContent className="p-6 text-center">
                  <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Phone className="w-7 h-7 text-green-600" />
                  </div>
                  <h3 className="text-lg font-bold text-slate-900 mb-3">Telepon</h3>
                  <a href={`tel:${companyInfo.phone}`} className="text-slate-600 hover:text-blue-600 font-semibold text-lg transition-colors duration-300">
                    {companyInfo.phone}
                  </a>
                </CardContent>
              </Card>
              
              <Card className="bg-white border-2 border-slate-200 hover:border-blue-400 hover:shadow-lg transition-all duration-300">
                <CardContent className="p-6 text-center">
                  <div className="w-14 h-14 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Clock className="w-7 h-7 text-amber-600" />
                  </div>
                  <h3 className="text-lg font-bold text-slate-900 mb-3">Jam Operasional</h3>
                  <p className="text-slate-600 font-semibold">{companyInfo.businessHours}</p>
                </CardContent>
              </Card>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              {/* Contact Form */}
              <div>
                <h2 className="text-3xl font-bold text-slate-900 mb-6">Kirim Pesan</h2>
                <p className="text-slate-600 mb-8">Isi formulir di bawah ini dan tim kami akan segera menghubungi Anda</p>
                
                {submitted ? (
                  <div className="bg-green-50 border-2 border-green-500 rounded-lg p-8 text-center">
                    <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
                    <h3 className="text-2xl font-bold text-green-900 mb-2">Terima Kasih!</h3>
                    <p className="text-green-700">Pesan Anda telah berhasil dikirim. Kami akan segera menghubungi Anda.</p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                      <Label htmlFor="name" className="text-slate-700 font-semibold mb-2 block">Nama Lengkap *</Label>
                      <Input
                        id="name"
                        name="name"
                        type="text"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        className="w-full border-slate-300 focus:border-blue-500 focus:ring-blue-500"
                        placeholder="Masukkan nama lengkap"
                      />
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="email" className="text-slate-700 font-semibold mb-2 block">Email *</Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          required
                          value={formData.email}
                          onChange={handleChange}
                          className="w-full border-slate-300 focus:border-blue-500 focus:ring-blue-500"
                          placeholder="email@contoh.com"
                        />
                      </div>
                      
                      <div>
                        <Label htmlFor="phone" className="text-slate-700 font-semibold mb-2 block">Nomor Telepon *</Label>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          required
                          value={formData.phone}
                          onChange={handleChange}
                          className="w-full border-slate-300 focus:border-blue-500 focus:ring-blue-500"
                          placeholder="08xx-xxxx-xxxx"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <Label htmlFor="subject" className="text-slate-700 font-semibold mb-2 block">Subjek *</Label>
                      <Input
                        id="subject"
                        name="subject"
                        type="text"
                        required
                        value={formData.subject}
                        onChange={handleChange}
                        className="w-full border-slate-300 focus:border-blue-500 focus:ring-blue-500"
                        placeholder="Topik pesan Anda"
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="message" className="text-slate-700 font-semibold mb-2 block">Pesan *</Label>
                      <Textarea
                        id="message"
                        name="message"
                        required
                        value={formData.message}
                        onChange={handleChange}
                        rows={6}
                        className="w-full border-slate-300 focus:border-blue-500 focus:ring-blue-500 resize-none"
                        placeholder="Tuliskan pesan atau pertanyaan Anda di sini..."
                      />
                    </div>
                    
                    <Button type="submit" size="lg" className="w-full bg-blue-600 hover:bg-blue-700 text-white py-6 text-lg rounded-lg shadow-lg hover:shadow-xl transition-all duration-300">
                      <Send className="w-5 h-5 mr-2" />
                      Kirim Pesan
                    </Button>
                  </form>
                )}
              </div>
              
              {/* Map */}
              <div>
                <h2 className="text-3xl font-bold text-slate-900 mb-6">Lokasi Kami</h2>
                <p className="text-slate-600 mb-6">Kunjungi kantor kami untuk konsultasi langsung</p>
                
                <Card className="bg-white border-2 border-slate-200 overflow-hidden shadow-lg">
                  <CardContent className="p-0">
                    <div className="relative w-full" style={{ paddingBottom: '75%' }}>
                      <iframe
                        src={companyInfo.mapEmbed}
                        width="100%"
                        height="100%"
                        style={{ border: 0, position: 'absolute', top: 0, left: 0 }}
                        allowFullScreen=""
                        loading="lazy"
                        referrerPolicy="no-referrer-when-downgrade"
                        title="Lokasi PT. Ids Sun Teknik"
                        className="rounded-t-lg"
                      ></iframe>
                    </div>
                    <div className="p-6">
                      <p className="text-sm text-slate-600 mb-4">
                        <strong className="text-slate-900">Kode Lokasi:</strong> {companyInfo.locationCode}
                      </p>
                      <div className="flex flex-col sm:flex-row gap-3">
                        <a
                          href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(companyInfo.address)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex-1 inline-flex items-center justify-center bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold transition-all duration-300 shadow-md hover:shadow-lg"
                        >
                          <MapPin className="w-5 h-5 mr-2" />
                          Petunjuk Arah
                        </a>
                        <a
                          href={`tel:${companyInfo.phone}`}
                          className="flex-1 inline-flex items-center justify-center bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-semibold transition-all duration-300 shadow-md hover:shadow-lg"
                        >
                          <Phone className="w-5 h-5 mr-2" />
                          Hubungi
                        </a>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
